import React, { createContext, useState, useEffect, useContext } from "react";
import { useAuth } from "./AuthContext";

const UserRoleContext = createContext();

export const useUserRole = () => {
  const context = useContext(UserRoleContext);
  if (!context) {
    throw new Error("useUserRole must be used within a UserRoleProvider");
  }
  return context;
};

export const UserRoleProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!currentUser) {
        setUserRole(null);
        setUserData(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      const maxAttempts = 4;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (cancelled) return;

        try {
          const response = await fetch("/api/auth/user", {
            credentials: "include",
            cache: "no-store",
          });
          const data = await response.json();

          if (data.success && data.user) {
            if (cancelled) return;
            setUserRole(data.user.role);
            setUserData(data.user);
            setLoading(false);
            return;
          }

          const errMsg = data.error || "Failed to fetch user data";
          if (attempt < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, 200 * (attempt + 1)));
            continue;
          }
          if (cancelled) return;
          setError(errMsg);
          setLoading(false);
          return;
        } catch (err) {
          console.error("Error fetching user role:", err);
          if (attempt < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, 200 * (attempt + 1)));
            continue;
          }
          if (cancelled) return;
          setError(err.message);
          setLoading(false);
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const value = {
    userRole,
    userData,
    loading,
    error,
    isBarOwner: userRole === "bar_owner",
    isBarAdmin: userRole === "bar_admin",
    isArtist: userRole === "artist",
    // MySQL returns BOOLEAN as 0/1, so we need to check for both 1 and true
    hasCompletedSetup:
      userData?.has_completed_setup === 1 ||
      userData?.has_completed_setup === true,
    barCount: userData?.bar_count || 0,
  };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};
